import { zeroAddress } from 'viem'
import type { TermMarketConfig } from '@1delta/data-sdk'
import { MorphoGeneralPublicResponse } from '../../../types/lender/morpho-types'
import { AdditionalYields, GenericTokenList } from '../../../types'
import type {
  FixedTermAuction,
  FixedTermFillNow,
  FixedTermInfo,
} from '../../../types/lenderTypes'
import { createMarketUid } from '../../../utils'
import { toGenericPriceKey, toOracleKey } from '../../../assets'
import { liquidationPenaltyFromLltv } from '../morpho/utils/parsers'
import {
  termMaintenanceRatioToLtv,
  termLiquidatedDamagesToPenalty,
} from './math'
import type {
  TermAuctionWindow,
  TermBookLevel,
  TermFillNow,
  TermMarketRaw,
} from './types'

const nowSec = () => Math.floor(Date.now() / 1000)

/** Synthesized per-market lender key, e.g. `TERM_FINANCE_<TERM_REPO_ID_HEX_UPPER>`. */
export function termLenderKey(termRepoId: string): string {
  const body = termRepoId.startsWith('0x') ? termRepoId.slice(2) : termRepoId
  return 'TERM_FINANCE_' + body.toUpperCase()
}

/** "3 days" / "5 hours" / "20 minutes" — for the human-readable implications. */
function fmtDuration(secs: number): string {
  if (secs >= 2 * 86400) return `${Math.round(secs / 86400)} days`
  if (secs >= 2 * 3600) return `${Math.round(secs / 3600)} hours`
  return `${Math.max(1, Math.round(secs / 60))} minutes`
}

/**
 * Consequences of Term's origination model that a rate alone doesn't convey.
 * Mirrors `params.market.teller.implications` — most decision-relevant first.
 */
function auctionImplications(
  status: FixedTermAuction['status'],
  matured: boolean,
  secondsUntilClose?: number,
  fillNowBorrow = false,
): string[] {
  const out: string[] = []
  if (matured) {
    out.push(
      'This term has MATURED — it can no longer be borrowed or lent. Existing positions settle through the repurchase/redemption window.',
    )
    return out
  }
  if (fillNowBorrow) {
    out.push(
      'Borrowing can be filled INSTANTLY against standing limit orders — the shown borrow rate is the best standing order and is the rate you actually get.',
    )
  }
  if (status === 'open') {
    out.push(
      `${fillNowBorrow ? 'Alternatively, borrowing' : '⚠ Borrowing'} is a SEALED BID into an auction${fillNowBorrow ? '' : ', not an instant loan'}. Submissions close in ${
        secondsUntilClose != null
          ? fmtDuration(secondsUntilClose)
          : 'a limited window'
      }${fillNowBorrow ? '.' : '; after that nothing can be borrowed until the next round is listed.'}`,
    )
    if (!fillNowBorrow)
      out.push(
        'Your rate is NOT the rate shown — the shown figure is the last round’s clearing rate. Your actual rate is set when this round clears, and a bid above the clearing rate may not be filled at all.',
      )
  } else if (status === 'revealing') {
    out.push(
      `⚠ Bidding has CLOSED for this round — sealed prices are being revealed and the auction is clearing.${fillNowBorrow ? '' : ' No new borrows until the next round is listed.'}`,
    )
  } else if (status === 'upcoming') {
    out.push(
      `An auction round is scheduled but not yet accepting bids.${fillNowBorrow ? '' : ' Borrowing becomes possible when it opens.'}`,
    )
  } else if (!fillNowBorrow) {
    out.push(
      '⚠ NO auction is open and no instant limit-order liquidity is standing, so this market CANNOT be borrowed right now.',
    )
    out.push(
      'Any borrow rate shown is the last round’s clearing rate — historical, not obtainable.',
    )
  }
  out.push(
    'Lending is still possible between rounds by buying repo tokens on the secondary market; only borrowing depends on the auction window or standing limit orders.',
  )
  out.push(
    'Fixed maturity: the debt is a static face value with no interest accrual, repayable up to the end of the repurchase window.',
  )
  return out
}

/**
 * Project a fetched auction round onto the canonical `FixedTermAuction`.
 *
 * `status` is re-derived from `now` (not taken from the fetch) so a snapshot
 * replayed from cache degrades to `revealing`/`closed` instead of claiming an
 * open window that has since passed. `canBorrow` / `canLend` are the flags
 * consumers should gate CTAs on, and they differ: only borrowing needs the
 * auction (see the type docs).
 */
export function toFixedTermAuction(
  auction: TermAuctionWindow | null | undefined,
  now: number,
  matured = false,
  fillNow?: TermFillNow | null,
): FixedTermAuction {
  // `canBorrow`/`canLend` answer "can a NEW position be opened right now, by
  // ANY path" — with Terminal 1 a standing limit order is such a path, so
  // fill-now liquidity flips them independently of the round `status`.
  const fillNowBorrow = !matured && !!fillNow?.borrow
  const base = { canBorrow: fillNowBorrow, canLend: !matured }
  if (!auction || auction.endTime <= now) {
    return {
      status: 'closed',
      ...base,
      implications: auctionImplications(
        'closed',
        matured,
        undefined,
        fillNowBorrow,
      ),
    }
  }
  const status: FixedTermAuction['status'] =
    now < auction.startTime
      ? 'upcoming'
      : now < auction.revealTime
        ? 'open'
        : 'revealing'
  const secondsUntilClose =
    status === 'open' ? Math.max(0, auction.revealTime - now) : undefined
  return {
    status,
    ...base,
    // A matured repo is never borrowable, whatever the round says.
    canBorrow: (status === 'open' || fillNowBorrow) && !matured,
    secondsUntilClose,
    implications: auctionImplications(
      status,
      matured,
      secondsUntilClose,
      fillNowBorrow,
    ),
    id: auction.id,
    startTime: auction.startTime,
    revealTime: auction.revealTime,
    endTime: auction.endTime,
    minBorrowAmount: auction.minBidAmount,
    minLendAmount: auction.minOfferAmount,
  }
}

function currencyFor(
  address: string,
  decimals: number,
  tokens: GenericTokenList,
): any {
  const lower = address.toLowerCase()
  return tokens[lower] ?? { address: lower, symbol: '', name: '', decimals }
}

/** The CTA-gating fill-now summary emitted on `fixedTerm.fillNow`. */
function toFixedTermFillNow(
  fillNow: TermFillNow | null | undefined,
  canLendAtAll: boolean,
): FixedTermFillNow | undefined {
  if (!fillNow || (!fillNow.borrow && !fillNow.lend)) return undefined
  return {
    canBorrow: !!fillNow.borrow,
    canLend: canLendAtAll && !!fillNow.lend,
    borrowAprPct: fillNow.borrow?.aprPct,
    lendAprPct: fillNow.lend?.aprPct,
    borrowLiquidity: fillNow.borrow?.assets,
    lendLiquidity: fillNow.lend?.assets,
  }
}

/**
 * Merge the legacy venue book (secondary listings, constant clearing APR) with
 * the Terminal 1 fill-now levels (real per-order rates) into one best-first
 * book: asks by APR descending (best lend first), bids ascending (cheapest
 * borrow first). Returns undefined when both sources are empty.
 */
function mergeTermBook(
  book: { bids: TermBookLevel[]; asks: TermBookLevel[] } | undefined,
  fillNow: TermFillNow | undefined,
): { bids: TermBookLevel[]; asks: TermBookLevel[] } | undefined {
  if (!book && !fillNow) return undefined
  const asks = [...(book?.asks ?? []), ...(fillNow?.lend?.levels ?? [])].sort(
    (a, b) => b.apr - a.apr,
  )
  const bids = [...(book?.bids ?? []), ...(fillNow?.borrow?.levels ?? [])].sort(
    (a, b) => a.apr - b.apr,
  )
  if (asks.length === 0 && bids.length === 0) return undefined
  return { bids, asks }
}

/**
 * Map fetched Term repos into the shared `MorphoGeneralPublicResponse` shape
 * (identical to Midnight), keyed by the synthesized `TERM_FINANCE_<id>` lender
 * key. One LOAN entry carries the fixed rates (`variableBorrowRate` = auction
 * borrow APR, `depositRate` = lend APR) + order-book depth as liquidity; one
 * COLLATERAL entry per leg carries the maintenance-ratio → collateralFactor +
 * liquidated-damages → penalty. `params.market.fixedTerm.model = 'term'`.
 */
export function convertTermMarketsToResponse(
  raw: TermMarketRaw[],
  chainId: string,
  prices: { [asset: string]: number } = {},
  _additionalYields: AdditionalYields = {
    intrinsicYields: {},
    lenderRewards: {},
    loaded: true,
  },
  tokens: GenericTokenList = {},
): { [m: string]: MorphoGeneralPublicResponse } {
  const out: { [m: string]: MorphoGeneralPublicResponse } = {}
  const now = nowSec()

  for (const { config, top, book, auction, fillNow: fillNowRaw } of raw) {
    if (!config?.termRepoId || !config.purchaseToken) continue

    const m = termLenderKey(config.termRepoId)
    const maturity = Number(config.maturity)
    const ttm = maturity - now
    const matured = ttm <= 0
    const fillNow = matured ? null : (fillNowRaw ?? null)

    // Rate precedence: a Terminal 1 standing order IS the rate the user gets,
    // so the fill-now best beats the last auction clearing rate (which between
    // rounds is historical) on the borrow side. On the lend side both venues
    // are actionable (secondary repo-token book vs filling a borrow order), so
    // the better of the two wins.
    const supplyAprPct = !matured
      ? Math.max(top?.supplyAprPct ?? 0, fillNow?.lend?.aprPct ?? 0)
      : 0
    const borrowAprPct = !matured
      ? (fillNow?.borrow?.aprPct ?? top?.borrowAprPct ?? 0)
      : 0

    // Term borrow origination has TWO paths: the primary sealed-bid auction
    // round, and (Terminal 1) instantly filling a standing maker lend order.
    // `auctionInfo.canBorrow` reflects both; `status` stays the round state.
    const auctionInfo = toFixedTermAuction(auction, now, matured, fillNow)
    const roundOpen = auctionInfo.status === 'open' && !matured
    const canBorrowNow = auctionInfo.canBorrow

    // Fixed-term rate card: each Term repo has exactly ONE maturity → single-
    // term product. Emitted when a borrow is actually openable: fill-now rates
    // are executable as-quoted; an open auction round quotes the last clearing
    // rate as the indicative figure (the sealed bid sets the real one).
    const cardApr = fillNow?.borrow ? fillNow.borrow.aprPct : borrowAprPct
    const terms =
      canBorrowNow && cardApr > 0 && (fillNow?.borrow || roundOpen)
        ? [
            {
              termId: 0,
              durationSecs: Math.max(0, ttm),
              durationDays: ttm / 86400,
              apr: cardApr,
            },
          ]
        : undefined

    const loanAddr = config.purchaseToken.toLowerCase()
    const loanAsset = tokens[loanAddr]
    const loanKey =
      toOracleKey(loanAsset?.assetGroup!) ||
      toGenericPriceKey(loanAddr, chainId)
    const loanPrice = prices[loanKey] ?? 0
    // Fill-now depth adds to the venue depth: both are real, disjoint books
    // (secondary listings + auction vs Terminal 1 standing orders).
    const supplyLiquidity =
      (top?.supplyLiquidity ?? 0) + (fillNow?.lend?.assets ?? 0)
    const borrowLiquidity =
      (top?.borrowLiquidity ?? 0) + (fillNow?.borrow?.assets ?? 0)

    // @ts-ignore — built incrementally, matches Morpho/Midnight converter pattern
    const entry: MorphoGeneralPublicResponse = { data: {} }

    // 1. LOAN entry (the borrow/lend market)
    const loanUid = createMarketUid(chainId, m, loanAddr)
    entry.data[loanUid] = {
      marketUid: loanUid,
      name: 'Loan ' + (tokens[loanAddr]?.symbol ?? ''),
      poolId: loanAddr,
      underlying: loanAddr,
      asset: currencyFor(loanAddr, config.loanDecimals, tokens),
      totalDeposits: supplyLiquidity,
      totalDebtStable: 0,
      totalDebt: 0,
      totalLiquidity: borrowLiquidity,
      borrowLiquidity,
      totalLiquidityUSD: borrowLiquidity * loanPrice,
      borrowLiquidityUSD: borrowLiquidity * loanPrice,
      totalDepositsUSD: supplyLiquidity * loanPrice,
      totalDebtStableUSD: 0,
      totalDebtUSD: 0,
      utilization: 0,
      depositRate: supplyAprPct,
      variableBorrowRate: borrowAprPct,
      rateModel: 'fixedTerm',
      // Term is fixed-rate: mirror the fixed borrow APR on stableBorrowRate too.
      stableBorrowRate: borrowAprPct,
      intrinsicYield: 0,
      rewards: undefined,
      decimals: config.loanDecimals,
      config: {
        0: {
          category: 0,
          borrowCollateralFactor: 0,
          collateralFactor: 0,
          borrowFactor: 1,
          liquidationPenalty: 0,
          closeFactor: 1,
          collateralDisabled: true,
          debtDisabled: false,
        },
      },
      closeFactor: 1,
      collateralActive: false,
      // Borrow needs a live path: an OPEN auction round OR standing Terminal 1
      // lend orders to fill. `depositsEnabled` stays on the maturity alone:
      // lending also works between rounds via the secondary repo-token book.
      borrowingEnabled: canBorrowNow,
      depositsEnabled: auctionInfo.canLend,
      hasStable: false,
      // Term has no variable rate at all; between auctions there is no
      // borrowable rate either, so flag it rather than let a 0 read as free.
      variableBorrowDisabled: true,
      isActive: true,
      isFrozen: false,
    } as any

    // 2. COLLATERAL entries — one per leg (index = on-chain collateral index)
    config.collateralParams.forEach((c) => {
      const collAddr = c.token.toLowerCase()
      const ltv = termMaintenanceRatioToLtv(c.maintenanceRatio)
      // OPEN-position bound: the servicer enforces the INITIAL margin ratio at
      // mint, which is stricter than maintenance (e.g. 1.1236 vs 1.0929 on the
      // PT-reUSD repo → 89.0% vs 91.5%). Publishing the maintenance-derived
      // LTV as the borrow bound made every max-loop calculation overestimate,
      // and the carry builder's sufficiency check then refused the "max".
      const initialLtv = c.initialRatio
        ? termMaintenanceRatioToLtv(c.initialRatio)
        : ltv
      const liquidationPenalty =
        termLiquidatedDamagesToPenalty(c.liquidatedDamages) ||
        liquidationPenaltyFromLltv(ltv)
      const collUid = createMarketUid(chainId, m, collAddr)
      entry.data[collUid] = {
        marketUid: collUid,
        name: 'Collateral ' + (tokens[collAddr]?.symbol ?? ''),
        poolId: collAddr,
        underlying: collAddr,
        asset: currencyFor(collAddr, c.decimals, tokens),
        totalDeposits: 0,
        totalDebtStable: 0,
        totalDebt: 0,
        totalLiquidity: 0,
        borrowLiquidity: 0,
        totalLiquidityUSD: 0,
        borrowLiquidityUSD: 0,
        totalDepositsUSD: 0,
        totalDebtStableUSD: 0,
        totalDebtUSD: 0,
        utilization: 0,
        depositRate: 0,
        variableBorrowRate: 0,
        stableBorrowRate: 0,
        intrinsicYield: 0,
        rewards: undefined,
        decimals: c.decimals,
        config: {
          0: {
            category: 0,
            borrowCollateralFactor: initialLtv,
            collateralFactor: ltv,
            borrowFactor: 1,
            liquidationPenalty,
            closeFactor: 1,
            collateralDisabled: false,
            debtDisabled: true,
          },
        },
        closeFactor: 1,
        collateralActive: true,
        borrowingEnabled: false,
        depositsEnabled: !matured,
        hasStable: false,
        isActive: true,
        isFrozen: false,
      } as any
    })

    const primaryCollateral = config.collateralParams[0]
    entry.params = {
      market: {
        lender: m,
        name: config.name,
        loanDecimals: config.loanDecimals,
        collateralDecimals: primaryCollateral?.decimals,
        id: config.termRepoId,
        lltv: primaryCollateral?.maintenanceRatio ?? '0',
        oracle: zeroAddress,
        irm: zeroAddress,
        collateralAddress: primaryCollateral?.token ?? zeroAddress,
        loanAddress: loanAddr,
        terms,
        // Term-specific extras (consumed by calldata / worker-api resolvers)
        maturity: config.maturity,
        redemptionTimestamp: config.redemptionTimestamp,
        endOfRepurchaseWindow: config.endOfRepurchaseWindow,
        redemptionValue: config.redemptionValue,
        servicingFee: config.servicingFee,
        // Canonical cross-protocol fixed-term descriptor. Term: single calendar
        // maturity, auction-cleared fixed rate, no early-repay penalty on the
        // lender (exit = sell the repo token on the secondary book).
        fixedTerm: {
          model: 'term',
          maturity: Number(config.maturity),
          fees: {},
          earlyRepay: { kind: 'none' },
          // Fixed rate is discovered by the periodic sealed-bid auction; the
          // per-repo servicer is the market-level venue reference.
          provider: { kind: 'auction', address: config.servicer },
          // The origination window. `closed` (the common state between rounds)
          // used to mean display-only; with Terminal 1, `canBorrow` can be true
          // while the round is closed — `fillNow` carries the instant path.
          auction: auctionInfo,
          fillNow: toFixedTermFillNow(fillNow, auctionInfo.canLend),
        } as FixedTermInfo,
        collateralParams: config.collateralParams,
        // Bounded book slice (top-N open orders/side) for later filtering.
        // `asks` = lend-side venues (secondary repo-token orders at the
        // clearing APR + Terminal 1 borrow orders at their own rates, best
        // rate first); `bids` = borrow-side (Terminal 1 lend orders, cheapest
        // first — the legacy auction book has no standing bids).
        book: !matured
          ? mergeTermBook(book ?? undefined, fillNow ?? undefined)
          : undefined,
      } as any,
    }
    entry.chainId = chainId

    out[m] = entry
  }

  return out
}

export type { TermMarketConfig }
